import React, { Component } from "react"
import { Modal } from "react-bootstrap"
import DrugTestInfo from "./EventDetails/DrugTestInfo"
import TestSteps from "./EventDetails/TestSteps"
import OrderResultRecord from "../../api/OrderResults/OrderResultModel/OrderResultRecord"
import { Subscription } from "rxjs"
import { toast } from "react-toastify"
import { CurrentServiceDetails, UpdateServiceData } from "../../api/OrderResults/CurrentServiceDetails"
import ServiceDetails from "../../api/OrderResults/OrderResultModel/ServiceDetails"
import { ImplementationTypeGovt, StorageKey } from "../../config/Constants"
import TestEventUpdateData from "../../api/OrderResults/OrderResultModel/TestEventUpdateData"
import PhoneType from "../../api/PhoneType"
import { APIError } from "../../api/ObservableFromFetch"
import ConfirmationModal from "../ConfirmationModal"
import ActionButton from "../ActionButton"

interface EventDetailsModalProps {
  details: OrderResultRecord
  onHide: (updated: boolean) => void
  onRefreshGrid?: () => void
  onMerge?: () => void
}

interface EventDetailsModalState {
  data?: ServiceDetails
  loading: boolean
  saving: boolean
  editMode: boolean
  testEventData?: TestEventUpdateData
  oldRegulatoryModeID?: number,
  oldEmployeeCategoryID?: number,
  updated: boolean,
  showConfirmSave: boolean
}

export default class EventDetailsModal extends Component<EventDetailsModalProps, EventDetailsModalState> {
  subscription?: Subscription
  updateSubscription?: Subscription
  state: EventDetailsModalState = { data: undefined, loading: false, saving: false, editMode: false, testEventData: undefined, updated: false, showConfirmSave: false }
  isOnTrack: boolean = sessionStorage.getItem(StorageKey.ImplementationType) === ImplementationTypeGovt

  componentDidMount() {
    this.setState({ loading: true })
    const details = this.props.details
    this.subscription = CurrentServiceDetails({ TestDetailClinicalID: details.TestDetailClinicalID, TestDetailID: details.TestDetailID }).subscribe(
      (data: ServiceDetails) => this.setState({ data: data, loading: false }),
      (error: string) => {
        this.setState({ loading: false })
        toast.error(error)
      },
      () => this.setState({ loading: false })
    )
  }

  handleAPIError = (error: Error | APIError) => {
    error && toast.error((error as APIError)?.errorData?.message || error.message)
  }

  handleEditModeChanged = (editMode: boolean) => {
    const { details: {HomePhone, WorkPhone, CellPhone} } = this.props
    const { CCFPersonnelID, CCFFirstName, CCFLastName } = this.state.data || {}

    const regulatoryModeID = this.state.data?.EventData?.RegulatoryModeID ?? 0
    const employeeCategoryID = this.state.data?.EventData?.EmployeeCategoryID ?? 0

    this.setState({
      editMode: editMode,
      testEventData: {
        PersonnelID: CCFPersonnelID || "",
        FirstName: CCFFirstName || "",
        LastName: CCFLastName || "",
        Phones: {
          [PhoneType.Home]: { number: HomePhone, isRequired: false },
          [PhoneType.Work]: { number: WorkPhone, isRequired: true },
          [PhoneType.Cell]: { number: CellPhone, isRequired: false },
        },
        RegulatoryModeID: regulatoryModeID,
        EmployeeCategoryID: employeeCategoryID,
      },
      oldRegulatoryModeID: regulatoryModeID,
      oldEmployeeCategoryID: employeeCategoryID
    })
  }

  handleUpdateFields = (testEventData: TestEventUpdateData) => {
    this.setState({ testEventData: testEventData })
  }

  saveChanges = () => {
    this.setState({ saving: true })
    this.updateSubscription && this.updateSubscription.unsubscribe()
    this.updateSubscription = UpdateServiceData(
      { ...this.state.testEventData as TestEventUpdateData,
        TestEventID: this.props.details.TestEventID,
        IDType: this.props.details.IDType,
        FormFoxTestAN: this.props.details.FormFoxTestAN
      }).subscribe(
      (data) => {
        toast.success(data)
        this.setState({ updated: true, saving: false }, () => this.props.onHide(this.state.updated))
      },
      (error) => {
        this.setState({ saving: false })
        this.handleAPIError(error)
        this.props.onHide(this.state.updated)
      })
  }

  handleSaveChanges = () => {
    const { oldEmployeeCategoryID, oldRegulatoryModeID, testEventData } = this.state

    if(oldEmployeeCategoryID !== testEventData?.EmployeeCategoryID || oldRegulatoryModeID !== testEventData?.RegulatoryModeID) {
      this.setState({showConfirmSave: true})
    } else {
      this.saveChanges()
    }
  }

  onHide = (collected?: boolean) => {
    if (collected) {
      this.props.onHide(this.state.updated)
      this.props.onRefreshGrid?.()
    }
  }

  handleConfirmSave = () => {
    this.setState({showConfirmSave:false});
    this.saveChanges();
  }

  handleConfirmClose = () => {
    this.setState({showConfirmSave:false});
  }

  isPhonesValid = () => {
    const { Phones } = this.state.testEventData || {}
    
    return (!Phones ||
      Object.values(Phones).every(phone =>
        !phone.isRequired ||
        phone.number.replace(/\D/g, "").length === 10)
      )
  }

  isValid = () => {
    return (
      this.state.testEventData && 
      this.state.testEventData.FirstName.length > 0 &&
      this.state.testEventData.LastName.length > 0 && 
      this.state.testEventData?.PersonnelID.length > 0 && 
      this.isPhonesValid()
    )
  }

  componentWillUnmount() {
    this.subscription && this.subscription.unsubscribe()
    this.updateSubscription && this.updateSubscription.unsubscribe()
  }

  render() {
    const currentRowInfo = this.props.details
    const currentServiceInfo = this.state.data
    const { saving}  = this.state
    return currentRowInfo && currentServiceInfo ? (<>
      <Modal show onHide={() => this.props.onHide(this.state.updated)} size="xl" dialogClassName="dark">
        <Modal.Header closeButton>
          <Modal.Title>VIEW/EDIT EVENT DETAILS</Modal.Title>
        </Modal.Header>
        <Modal.Body className="event-details">
          <DrugTestInfo
            currentRowInfo={currentRowInfo}
            updateFields={this.handleUpdateFields}
            currentServiceInfo={currentServiceInfo}
            isOnTrack={this.isOnTrack}
            editModeChanged={this.handleEditModeChanged}
            onMerge={this.props.onMerge}
            onHide={this.onHide} />
          <TestSteps currentRowInfo={currentRowInfo} currentServiceInfo={currentServiceInfo} isOnTrack={this.isOnTrack} />
        </Modal.Body>
        {this.state.editMode && <Modal.Footer>
          <ActionButton variant="primary" onClick={this.handleSaveChanges} disabled={!this.isValid() || saving} spin={saving} disableOnSpin>Save</ActionButton>
        </Modal.Footer>}
      </Modal>
      <ConfirmationModal show={this.state.showConfirmSave}
        title="CONFIRM"
        textTitle="Please also update the information with the lab to ensure laboratory reports are accurate and reflect the saved changes."
        text=""
        showCancelButton={false}
        showImage={false}
        confirmButtonText="OK"
        onConfirm={this.handleConfirmSave}
        onHide={this.handleConfirmClose} />
      </>
    ) : null
  }
}
